<template>
  <div>
    <core-modal-header>
      {{heading}}

      <div v-if="step === 1" class="flex mt-3 gap-2 -mr-5 -mb-1 font-sans font-normal tracking-normal">
        <div class="sm:flex-1">
          <people-name-status-select v-model="namedStatus" />
        </div>
        <div class="flex items-center gap-2 flex-1 sm:flex-0 justify-end">
          <core-sort-select-menu v-if="!searchTerm" v-model="currentSort" :options="PEOPLE_SORT_OPTIONS" />
          <core-search-input
            placeholder="Search People"
            @search="updateSearchQuery"
            @clear="updateSearchQuery({q: ''})"
            :loading="pending"
          />
        </div>
      </div>
    </core-modal-header>

    <template v-if="step === 1">
      <core-modal-body>
        <div class="overflow-y-auto scrollbar-light -mr-2 sm:-mr-4 pr-2 sm:pr-4 scrollbar-gutter-stable max-h-[60vh]" ref="listEl">
          <transition-group name="f-slide-fade">
            <core-contextual-loading-box v-if="pending" key="loading" loading-text="Loading People" />
            <core-contextual-error-box v-else-if="error" key="error" :action="refresh" />

            <core-empty-state
              v-else-if="!filteredPeople.length"
              heading="No People"
              description="No people were found."
              :icon="COMMON_ICONS.person"
            />

            <core-list v-else :items="filteredPeople" :scroller-elem="listEl" :disable-approach-bottom="pending || isLoadingMore || !hasMore" @approach-bottom="getMore" container-class="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6" grid-gap-class="gap-3" item-class="relative" :section-size="30">
              <template #default="{ item: person }">
                <people-list-item
                  :selected="selection.has(person)"
                  :person="person"
                  :key="person.id"
                  disable-edit
                  show-file-count
                  @item-click="toggleItem(person)"
                />
              </template>
            </core-list>
          </transition-group>
        </div>
      </core-modal-body>

      <core-modal-footer>
        <u-button size="xl" @click="() => modal.close()" variant="soft" color="charcoal">Cancel</u-button>
        <u-button size="xl" @click="step = 2" :disabled="!selectedPeople?.length">Continue to Review</u-button>
      </core-modal-footer>
    </template>

    <template v-else>
      <core-modal-body>

        <p class="text-xl text-center mb-2">Are you sure you want to merge...</p>

        <core-list :items="selectedPeople" container-class="flex flex-wrap justify-center" grid-gap-class="gap-0" item-class="w-1/3 sm:w-1/5 md:w-1/6 relative p-1.5" :section-size="30">
          <template #default="{ item: person }">
            <people-list-item
              :selected="selection.has(person)"
              :person="person"
              :key="person.id"
              disable-edit
              show-file-count
              @item-click="toggleItem(person)"
            />
          </template>
        </core-list>

        <u-divider label="into" icon="i-ri-arrow-right-line" class="my-4" :ui="{label: 'text-lg font-semibold'}" />

        <div class="max-w-40 mx-auto mb-6">
          <people-list-item
            :person="person"
            disable-edit
          />
        </div>

        <u-alert
          color="red"
          :icon="COMMON_ICONS.information"
          title="This action cannot be undone."
          description="This action will permanently combine the selected faces with the destination person. All information you've added to the selected people will be lost."
        />
      </core-modal-body>

      <core-modal-footer>
        <u-button size="xl" @click="() => modal.close()" variant="soft" color="charcoal">Cancel</u-button>
        <u-button size="xl" @click="step = 1" variant="soft">Back</u-button>
        <u-button size="xl" @click="merge" :disabled="!selectedPeople?.length" :loading="isSaving">
          Merge People
          <template v-if="isSaving">{{` (${mergeProgress}/${selectedPeople.length})`}}</template>
        </u-button>
      </core-modal-footer>
    </template>

  </div>

</template>

<script setup>
  import {makePeopleStore} from '~/stores/people.js';
  import {storeToRefs} from 'pinia';
  import {useStorage} from '@vueuse/core';

  const props = defineProps({
    person: Object,
    modal: Object
  });

  const BULK_MERGE_LIMIT = 18;

  const emit = defineEmits(['success']);

  const step = ref(1);

  const listEl = ref();

  const peopleStoreId = 'showHidePeopleStore';
  const peopleStore = makePeopleStore(peopleStoreId)();
  const {people, hasMore} = storeToRefs(peopleStore);
  const selection = useSelection(
    'people-bulk-merge-selection',
    {
      maxSelection: BULK_MERGE_LIMIT,
      onMaxSelectionReached() {
        useInfoToast().add({description: `You can only merge up to ${BULK_MERGE_LIMIT} people at a time.`});
      }
    }
  );

  const isSaving = ref(false);

  const namedStatus = ref(PEOPLE_NAMED_STATUS_OPTIONS[0]);

  const storedSort = useStorage(
    'core-web-ui-people-sort',
    PEOPLE_SORT_OPTIONS[0]
  );

  const currentSort = ref(storedSort.value);

  //search
  const searchTerm = ref();
  function updateSearchQuery({q}) {
    searchTerm.value = q;
  }

  watch(step, () => {
    searchTerm.value = null
  });

  const {refresh, pending, error} = await useLazyAsyncData(
    'bulk-merge',
    () => peopleStore.getPeople({
      displayStatus: [PEOPLE_DISPLAY_STATUS.visible],
      search: searchTerm.value,
      namedStatus: namedStatus.value.param,
      order: currentSort.value.param
    }),
    {
      watch: [searchTerm, namedStatus, currentSort]
    }
  );

  const isLoadingMore = ref(false);

  async function getMore() {
    if (!hasMore.value) {
      return;
    }

    isLoadingMore.value = true;

    try {
      await peopleStore.getPeople({nextPage: true});
    } catch (e) {
      useErrorToast().add({description: 'There was an issue getting more people.'});
    } finally {
      isLoadingMore.value = false;
    }
  }

  function toggleItem(person) {
    if (!isSaving.value) {
      selection.toggle({item: person});
    }
  }

  const heading = computed(() => {
    if (step.value === 1) {
      return `Step 1: Choose Faces to Merge with ${props.person.name || 'Unnamed'}`;
    } else if (step.value === 2) {
      return 'Step 2: Review and Merge';
    }
  });

  const filteredPeople = computed(() => people?.value?.filter(person => !person.isCurrentUser && person.id !== props.person.id && person.images));
  const selectedPeople = computed(() => selection.selected.value);
  let mergeProgress = ref(0);

  async function merge() {
    let failureCount = 0;

    isSaving.value = true;
    for (let i = 0; i < selectedPeople.value.length; i++) {
      const person = selectedPeople.value[i];
      try {
        await usePeopleApi().mergePeople({
          source: person,
          destination: props.person
        });
      } catch (e) {
        failureCount++;
        console.log(`Error merging person with ID ${person.id}:`, e);
      } finally {
        mergeProgress.value++;
      }
    }

    isSaving.value = false;
    emit('success', {error: failureCount || null});
  }

  onUnmounted(() => {
    peopleStore.clearPeople();
    selection.clear();
  });
</script>
